import { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  useActiveProject,
  useActiveProjectCohortId,
  useMixpanel,
  useNotify,
  useService,
} from '../../../../hooks';
import { CarePlanService } from '../../../../services/CarePlanService';
import {
  buildMinimalActionPlan,
  hasActiveStandardCarePlan,
} from './minimalActionPlan';

const actionPlanService = new CarePlanService();

const messages = defineMessages({
  createFailure: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.createFailure',
    defaultMessage: 'Could not start the action plan. Please try again.',
  },
});

export const useCreateActionPlan = ({
  patientId,
  carePlans,
  fetchCarePlan,
}: {
  patientId: string;
  carePlans: fhir.CarePlan[];
  fetchCarePlan: () => void;
}) => {
  const { formatMessage } = useIntl();
  const [notify] = useNotify();
  const mixpanel = useMixpanel();
  const [project] = useActiveProject();
  const cohort = useActiveProjectCohortId();
  const [createCarePlan] = useService(actionPlanService.createCarePlan, {
    rethrowErrors: true,
  });
  const [isCreating, setIsCreating] = useState(false);

  const createActionPlan = useCallback(async () => {
    if (hasActiveStandardCarePlan(carePlans)) {
      fetchCarePlan();
      return;
    }

    setIsCreating(true);
    try {
      await createCarePlan(
        buildMinimalActionPlan(patientId, project.id),
        cohort,
      );
      mixpanel.track('action_plan_created', { outcome: 'success' });
      fetchCarePlan();
    } catch {
      mixpanel.track('action_plan_created', { outcome: 'error' });
      notify(formatMessage(messages.createFailure), 'error');
    } finally {
      setIsCreating(false);
    }
  }, [
    carePlans,
    cohort,
    createCarePlan,
    fetchCarePlan,
    formatMessage,
    mixpanel,
    notify,
    patientId,
    project.id,
  ]);

  return { createActionPlan, isCreating };
};
